import classNames from "classnames";
import { Nav } from "react-bootstrap";
import {
  Plus,
  Person,
  BarChart,
  QrCode,
  Globe,
  Archive,
  CreditCard2Back,
  Coin,
  Headset,
  Gear,
  QuestionCircle,
  MenuButtonWideFill,
  Link45deg,
} from "react-bootstrap-icons";
import { useTranslation } from "react-i18next";
import { NavLink } from "react-router-dom";

import { useOffcanvas } from "@/components/context/OffcanvasContext";
import PathExpander from "@/components/ui/pathExpander";
import { BASE_URL } from "@/constants";
import { IS_ME_LINK, LANDING_URL } from "@/constants/app";
import { EXTERNAL_ROUTES, ROUTES } from "@/constants/routes";
import { isIosWebView, isWebView, webViewVersion } from "@/constants/webView";
import { currentFolderVar } from "@/graphql/globalVar/state";
import useChecksCountUserQrs from "@/hooks/checks/useChecksCountUserQrs";
import useLocalizedHref from "@/hooks/useLocalizedHref";
import navigationClickEvent from "@/services/analyticsEvents/navigationClickEvent";
import openNewGenEvent from "@/services/analyticsEvents/openNewGenEvent";
import { useAuthService } from "@/services/authService";
import webViewEventService from "@/services/webViewEvents/webViewEventService";
import { withSuspense } from "@/utils/hoc/withSuspense";

import Import from "../icons/import.svg?react";

import styles from "./Menu.module.scss";

const MenuFoldersLazy = withSuspense(() => import("@/components/menuFolders"));

function Menu() {
  const { t } = useTranslation();
  const localizedHref = useLocalizedHref();
  const offcanvas = useOffcanvas();
  const { isAdmin, isPremium } = useAuthService();
  const showWebOnlyItems = !(isWebView && (webViewVersion ?? 0) >= 3);
  const { limitReached } = useChecksCountUserQrs();
  const createBtnDisabled = !IS_ME_LINK && !isPremium && limitReached;

  return (
    <Nav variant="pills" className={styles.nav}>
      <Nav.Link
        as={IS_ME_LINK ? "a" : NavLink}
        {...(IS_ME_LINK
          ? {
              href: `${localizedHref(`${LANDING_URL}${EXTERNAL_ROUTES.HOME}`)}#heroShortLink`,
            }
          : {
              to: localizedHref(`${BASE_URL}${ROUTES.QR_CODE_GENERATOR}`),
              end: true,
            })}
        className={classNames(styles.item, "accent")}
        onClick={
          !IS_ME_LINK
            ? (e: React.MouseEvent) => {
                if (createBtnDisabled) {
                  e.preventDefault();
                  return;
                }
                openNewGenEvent({
                  label: "create_new_qr_code",
                  place: "admin-menu",
                });
                offcanvas.close();
              }
            : undefined
        }
        tabIndex={createBtnDisabled ? -1 : undefined}
        aria-disabled={createBtnDisabled || undefined}
        disabled={createBtnDisabled}
      >
        <Plus />
        <span>{IS_ME_LINK ? t("menu.item1link") : t("menu.item1")}</span>
      </Nav.Link>

      <Nav.Link
        as={NavLink}
        to={localizedHref(ROUTES.ENTRY)}
        end
        className={styles.item}
        onClick={() => {
          currentFolderVar(null);
          navigationClickEvent({ label: "my_qr_codes", place: "admin-menu" });
          offcanvas.close();
        }}
      >
        <QrCode />
        <span>{IS_ME_LINK ? t("menu.item4link") : t("menu.item4")}</span>
      </Nav.Link>

      <PathExpander path={ROUTES.ENTRY} mountOnEnter>
        <MenuFoldersLazy />
      </PathExpander>

      <Nav.Link
        as={NavLink}
        to={localizedHref(ROUTES.STATISTICS)}
        end
        className={styles.item}
        onClick={() => {
          navigationClickEvent({ label: "statistics", place: "admin-menu" });
          offcanvas.close();
        }}
      >
        <BarChart />
        <span>{t("menu.item3")}</span>
      </Nav.Link>

      {!isWebView && !IS_ME_LINK && (
        <Nav.Link
          as={NavLink}
          to={localizedHref(ROUTES.CSV_IMPORT)}
          end
          className={styles.item}
          onClick={() => {
            navigationClickEvent({
              label: "import_from_csv",
              place: "admin-menu",
            });
            offcanvas.close();
          }}
        >
          <Import />
          <span>{t("menu.item6")}</span>
        </Nav.Link>
      )}

      {!isWebView && IS_ME_LINK && (
        <Nav.Link
          as={NavLink}
          to={localizedHref(ROUTES.BULK_URL_SHORTENER)}
          end
          className={styles.item}
          onClick={() => {
            navigationClickEvent({
              label: "bulk_url_shortener",
              place: "admin-menu",
            });
            offcanvas.close();
          }}
        >
          <Link45deg />
          <span>{t("menu.item13")}</span>
        </Nav.Link>
      )}

      <Nav.Link
        as={NavLink}
        to={localizedHref(ROUTES.ARCHIVE)}
        end
        className={styles.item}
        onClick={() => {
          navigationClickEvent({
            label: "archive_qr_codes",
            place: "admin-menu",
          });
          offcanvas.close();
        }}
      >
        <Archive />
        <span>{IS_ME_LINK ? t("menu.item7link") : t("menu.item7")}</span>
      </Nav.Link>

      {!isWebView && (
        <Nav.Link
          as={NavLink}
          to={localizedHref(ROUTES.DOMAIN)}
          end
          className={styles.item}
          onClick={() => {
            navigationClickEvent({ label: "domain", place: "admin-menu" });
            offcanvas.close();
          }}
        >
          <Globe />
          <span>{IS_ME_LINK ? t("menu.item5link") : t("menu.item5")}</span>
        </Nav.Link>
      )}

      <Nav.Link
        as={NavLink}
        to={localizedHref(ROUTES.INTEGRATION)}
        end
        className={styles.item}
        onClick={() => {
          navigationClickEvent({
            label: "integration",
            place: "admin-menu",
          });
          offcanvas.close();
        }}
      >
        <Gear />
        <span>{t("menu.item11")}</span>
      </Nav.Link>

      {showWebOnlyItems && (
        <Nav.Link
          as={NavLink}
          to={localizedHref(ROUTES.PROFILE)}
          end
          className={styles.item}
          onClick={() => {
            navigationClickEvent({ label: "profile", place: "admin-menu" });
            offcanvas.close();
          }}
        >
          <Person />
          <span>{t("menu.item2")}</span>
        </Nav.Link>
      )}

      {!isIosWebView && (
        <Nav.Link
          as={NavLink}
          to={localizedHref(ROUTES.SUBSCRIPTION)}
          end
          className={styles.item}
          onClick={() => {
            navigationClickEvent({
              label: "payment_subscription",
              place: "admin-menu",
            });
            webViewEventService.linkToPricing();
            offcanvas.close();
          }}
        >
          <CreditCard2Back />
          <span>{t("menu.item8")}</span>
        </Nav.Link>
      )}

      {showWebOnlyItems && !IS_ME_LINK && (
        <Nav.Link
          as={NavLink}
          to={localizedHref(ROUTES.REFERRER)}
          end
          className={styles.item}
          onClick={() => {
            navigationClickEvent({
              label: "refer",
              place: "admin-menu",
            });
            offcanvas.close();
          }}
        >
          <Coin />
          <span>{t("menu.item9")}</span>
        </Nav.Link>
      )}

      <Nav.Link
        as={NavLink}
        to={localizedHref(ROUTES.SUPPORT)}
        end
        className={styles.item}
        onClick={() => {
          navigationClickEvent({
            label: "support",
            place: "admin-menu",
          });
          offcanvas.close();
        }}
      >
        <Headset />
        <span>{t("menu.item10")}</span>
      </Nav.Link>

      {!isWebView && !IS_ME_LINK && (
        <Nav.Link
          as={NavLink}
          to={localizedHref(ROUTES.FAQ)}
          end
          className={styles.item}
          onClick={() => {
            navigationClickEvent({
              label: "faq",
              place: "admin-menu",
            });
            offcanvas.close();
          }}
        >
          <QuestionCircle />
          <span>{t("menu.item12")}</span>
        </Nav.Link>
      )}

      {isAdmin && (
        <Nav.Link
          href={localizedHref(
            `${IS_ME_LINK ? LANDING_URL : BASE_URL}${EXTERNAL_ROUTES.OLD_ADMIN_SEARCH_USER}`
          )}
          className={styles.item}
          onClick={() => {
            offcanvas.close();
          }}
        >
          <MenuButtonWideFill />
          <span>Search User</span>
        </Nav.Link>
      )}
    </Nav>
  );
}

export default Menu;
